import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  CircularProgress,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
} from "@mui/icons-material";
import axios from "../../utils/axiosInstance";
import { toast } from "react-toastify";
import { sortAlphabetically } from "../../utils/sortAlphabetically";
import { getUIDescription } from "./customerGroupDetails/AssigningCustomerGroupDevice";

const ALLOWED_EMAILS = [
  "SOFTCITY002@GMAIL.COM",
  "WEB@PRECISION.COM",
  "RAYMOND@OPENAIRIOT.COM",
  "TINOD@PRECISIONCOMPRESSION.COM",
];

export default function EditAppForm({ open, onClose, app, onSubmit }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [logo, setLogo] = useState("");
  const [logoStatus, setLogStatus] = useState("#000000");
  const [url, setUrl] = useState("");
  const [groupId, setGroupId] = useState("");

  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");

  const [appType, setAppType] = useState("admin");
  const groupRequired = appType !== "admin";

  const [userEmail, setUserEmail] = useState(null);

  const [errors, setErrors] = useState({
    name: false,
    description: false,
    logo: false,
    logoStatus: false,
    url: false,
    groupId: false,
  });

    useEffect(() => {
      const userString = localStorage.getItem("user");
      if (userString) {
        const user = JSON.parse(userString);
        setUserEmail(user.email);
      }
    }, []);

const canSelectAppType = ALLOWED_EMAILS.includes(userEmail);

  useEffect(() => {
    const urlMap = {
      admin: `${process.env.REACT_APP_API_URL}/ZWAD`,
      user: `${process.env.REACT_APP_API_URL}/ZXUR`,
      viewOnly: `${process.env.REACT_APP_API_URL}/ZXVO`,
    };
    setUrl(urlMap[appType] ?? "");
  }, [appType]);

  // Populate form when "app" changes
  useEffect(() => {
    if (app) {
      setName(app.name || "");
      setDescription(app.description || "");
      setLogo(app.logo || "");
      setLogStatus(app.logoStatus || "#000000");
      setUrl(app.url || "");
      setGroupId(app.group?.id || "");
      setPreviewUrl(app.logo || "");

      if (app.url?.includes("/ZXUR")) setAppType("user");
      else if (app.url?.includes("/ZXVO")) setAppType("viewOnly");
      else setAppType("admin");
    }
  }, [app]);

  // Fetch groups
  useEffect(() => {
    if (!open) return;
    const fetchGroups = async () => {
      try {
        setLoadingGroups(true);
        const res = await axios.get(
          `${process.env.REACT_APP_API_GATEAWAY}/groups`,
          {
            params: {
              companyId: process.env.REACT_APP_COMPANY_ID,
              type: "customer",
            },
          },
        );
        const sortedData = sortAlphabetically(
          Array.isArray(res.data) ? res.data : [],
          "name",
        );
        setGroups(sortedData);
      } catch (err) {
        console.error("❌ Error fetching groups:", err);
      } finally {
        setLoadingGroups(false);
      }
    };
    fetchGroups();
  }, [open]);

  // Compress image function
  const compressImage = (file, maxSizeKB = 500) => {
    return new Promise((resolve, reject) => {
      // Skip compression for SVG files
      if (file.type === "image/svg+xml") {
        resolve(file);
        return;
      }

      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          // Calculate new dimensions (max 1920px width/height)
          let width = img.width;
          let height = img.height;
          const maxDimension = 1920;

          if (width > maxDimension || height > maxDimension) {
            if (width > height) {
              height = (height / width) * maxDimension;
              width = maxDimension;
            } else {
              width = (width / height) * maxDimension;
              height = maxDimension;
            }
          }

          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);

          // Start with high quality
          let quality = 0.9;
          const targetSizeBytes = maxSizeKB * 1024;

          const tryCompress = () => {
            canvas.toBlob(
              (blob) => {
                if (blob.size <= targetSizeBytes || quality <= 0.1) {
                  // Convert blob to file
                  const compressedFile = new File([blob], file.name, {
                    type: file.type === "image/png" ? "image/jpeg" : file.type,
                    lastModified: Date.now(),
                  });
                  resolve(compressedFile);
                } else {
                  // Reduce quality and try again
                  quality -= 0.1;
                  tryCompress();
                }
              },
              file.type === "image/png" ? "image/jpeg" : file.type,
              quality,
            );
          };

          tryCompress();
        };
        img.onerror = reject;
      };
      reader.onerror = reject;
    });
  };

  // Handle file selection
  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/svg+xml",
    ];
    if (!validTypes.includes(file.type)) {
      toast.error("Please upload a valid image file (JPEG, PNG, GIF, or SVG)");
      return;
    }

    try {
      // Show original size
      const originalSizeKB = (file.size / 1024).toFixed(2);
      console.log(`📊 Original size: ${originalSizeKB} KB`);

      // Compress the image
      const compressedFile = await compressImage(file, 500); // Target 500KB
      const compressedSizeKB = (compressedFile.size / 1024).toFixed(2);
      console.log(`📊 Compressed size: ${compressedSizeKB} KB`);

      if (compressedFile.size < file.size) {
        toast.success(
          `Image optimized: ${originalSizeKB}KB → ${compressedSizeKB}KB`,
        );
      }

      setSelectedFile(compressedFile);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(compressedFile);
    } catch (error) {
      console.error("❌ Error compressing image:", error);
      toast.error("Failed to process image. Please try again.");
    }
  };

  // Upload logo to server
  const handleUploadLogo = async () => {
    if (!selectedFile) {
      toast.warning("Please select a file first");
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      let FullDescription = `${getUIDescription()}\n Upload Logo Application`;
      formData.append("file", selectedFile);
      formData.append("folder", "logos");
      formData.append("UI_Description", FullDescription);
      const response = await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (response.data.success && response.data.url) {
        setLogo(response.data.url);
        setPreviewUrl(response.data.url);
        toast.success("✅ Logo uploaded successfully!");
        setErrors((prev) => ({ ...prev, logo: false }));
      } else {
        throw new Error("Upload failed");
      }
    } catch (error) {
      console.error("❌ Error uploading logo:", error);
      toast.error("Failed to upload logo. Please try again.");
    } finally {
      setUploadingLogo(false);
      setSelectedFile(null);
    }
  };

  // Remove logo
  const handleRemoveLogo = () => {
    setLogo("");
    setPreviewUrl("");
    setSelectedFile(null);
    setErrors((prev) => ({ ...prev, logo: false }));
  };

  // Validators
  const isValidUrl = (str) => {
    try {
      new URL(str);
      return true;
    } catch {
      return false;
    }
  };
  const isValidHexColor = (color) => /^#([0-9A-F]{3}){1,2}$/i.test(color);

  const validateForm = () => {
    const newErrors = {
      name: name.trim() === "",
      description: false,
      logo: logo.trim() !== "" && !isValidUrl(logo),
      logoStatus: logoStatus.trim() === "" || !isValidHexColor(logoStatus),
      url: url.trim() === "" || !isValidUrl(url),
      groupId: groupRequired && !groupId,
    };
    setErrors(newErrors);
    return !Object.values(newErrors).some(Boolean);
  };

  // Submit handler
  const handleUpdateApp = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const payload = {
        name,
        description,
        logo,
        logoStatus,
        url,
        groupId: groupRequired ? groupId || null : null,
        UI_Description: `${getUIDescription()}\n Update Application`,
      };

      // Compare with original app data to detect no changes
      const original = {
        name: app.name || "",
        description: app.description || "",
        logo: app.logo || "",
        logoStatus: app.logoStatus || "#000000",
        url: app.url || "",
        groupId: app.group?.id || "",
      };

      const hasChanges = Object.keys(payload).some(
        (key) => payload[key] !== original[key],
      );

      if (!hasChanges) {
        toast.info("No changes were made");
        setLoading(false);
        onClose();
        return;
      }

      // Proceed to update
      const response = await axios.put(
        `${process.env.REACT_APP_API_GATEAWAY}/applications/${app.id}`,
        payload,
      );

      toast.success("✅ Application updated successfully!");
      onSubmit?.(response.data);
      onClose();
    } catch (error) {
      const rawMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.response?.data ||
        error.message;

      let errorMessage = "Failed to update application";

      if (typeof rawMessage === "string") {
        const lowerMsg = rawMessage.toLowerCase();
        if (lowerMsg.includes("url")) {
          errorMessage = "Please enter a valid URL";
        } else if (lowerMsg.includes("group")) {
          errorMessage = "Please select a valid group";
        } else {
          errorMessage = rawMessage;
        }
      }

      toast.error(errorMessage);
      console.error("❌ Error updating app:", rawMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Application</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <TextField
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 0, // ✅ remove rounding
              },
            }}
            label="Name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (errors.name && e.target.value.trim()) {
                setErrors((prev) => ({ ...prev, name: false }));
              }
            }}
            fullWidth
            required
            error={errors.name}
            helperText={errors.name ? "Name is required" : ""}
          />

          <TextField
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 0, // ✅ remove rounding
              },
            }}
            label="Description"
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              if (errors.description && e.target.value.trim()) {
                setErrors((prev) => ({ ...prev, description: false }));
              }
            }}
            fullWidth
            multiline
            minRows={2}
            error={errors.description}
          />

          {/* Logo Upload Section */}
          <Box>
            <Typography
              variant="subtitle2"
              sx={{ mb: 1, fontWeight: 500, color: "text.secondary" }}
            >
              Upload Logo (Recommended Aspect Ratio: 4.5:1)
            </Typography>
            {/* Preview */}
            {previewUrl && (
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  maxWidth: 200,
                  height: 120,
                  mb: 2,
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#f5f5f5",
                }}
              >
                <img
                  src={previewUrl}
                  alt="Logo preview"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                  }}
                />
                <IconButton
                  size="small"
                  onClick={handleRemoveLogo}
                  sx={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    backgroundColor: "rgba(0,0,0,0.6)",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "rgba(0,0,0,0.8)",
                    },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            )}

            {/* File Input */}
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUploadIcon />}
                disabled={uploadingLogo}
              >
                Choose File
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleFileSelect}
                />
              </Button>

              {selectedFile && (
                <Button
                  variant="contained"
                  onClick={handleUploadLogo}
                  disabled={uploadingLogo}
                  startIcon={
                    uploadingLogo ? <CircularProgress size={16} /> : null
                  }
                >
                  {uploadingLogo ? "Uploading..." : "Upload"}
                </Button>
              )}
            </Box>

            {selectedFile && (
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ mt: 0.5 }}
              >
                Selected: {selectedFile.name}
              </Typography>
            )}

            {errors.logo && (
              <Typography
                variant="caption"
                color="error"
                sx={{ mt: 0.5, display: "block" }}
              >
                Please upload a valid logo
              </Typography>
            )}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <TextField
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 0, // ✅ remove rounding
                },
              }}
              label="Banner Color"
              type="color"
              value={logoStatus}
              onChange={(e) => {
                setLogStatus(e.target.value);
                if (errors.logoStatus && isValidHexColor(e.target.value)) {
                  setErrors((prev) => ({ ...prev, logoStatus: false }));
                }
              }}
              fullWidth
              required
              error={errors.logoStatus}
              helperText={
                errors.logoStatus
                  ? "Enter a valid color hex (e.g. #050505)"
                  : "Select a color for the banner"
              }
            />

            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "8px",
                border: "1px solid #ccc",
                backgroundColor: logoStatus,
              }}
            />
          </Box>

          <TextField
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 0, // ✅ remove rounding
              },
            }}
            label="App URL"
            value={url}
            fullWidth
            required
            disabled
          />

          {/* App Type */}
          {canSelectAppType && (
            <TextField
              select
              label="App Type"
              value={appType}
              onChange={(e) => {
                setAppType(e.target.value);
                if (e.target.value === "admin") setGroupId("");
              }}
              fullWidth
              required
            >
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="user">View & Control</MenuItem>
              <MenuItem value="viewOnly">View Only</MenuItem>
            </TextField>
          )}

          {/* Group */}
          <TextField
            select
            value={appType === "admin" ? "" : groupId}
            onChange={(e) => setGroupId(e.target.value)}
            fullWidth
            required={groupRequired}
            disabled={loadingGroups || appType === "admin"}
            error={errors.groupId}
            helperText={
              errors.groupId ? "Group is required for this app type" : ""
            }
            SelectProps={{ displayEmpty: true }}
          >
            <MenuItem value="">No group selected</MenuItem>
            {Array.isArray(groups) && groups.length > 0 ? (
              groups.map((group) => (
                <MenuItem key={group.id} value={group.id}>
                  {group.name}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>
                {loadingGroups ? "Loading groups..." : "No groups available"}
              </MenuItem>
            )}
          </TextField>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="error">
          Cancel
        </Button>
        <Button
          sx={{
            borderRadius: 0, // ✅ remove rounding
          }}
          onClick={handleUpdateApp}
          variant="contained"
          color="primary"
          disabled={
            loading ||
            loadingGroups ||
            uploadingLogo ||
            !name.trim() ||
            !logoStatus.trim() ||
            !url.trim() ||
            (groupRequired && !groupId)
          }
        >
          {loading ? "Saving..." : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
